import { type FC, type ReactNode } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { Option, Select } from 'base-ui'

import { SupportedLanguages } from '../../../../../tenant'

const getLabelText = (intl: IntlShape, language: string): string => {
  switch (language) {
    case SupportedLanguages.DE: {
      return intl.formatMessage({ id: 'general.label.german' })
    }
    case SupportedLanguages.FR: {
      return intl.formatMessage({ id: 'general.label.french' })
    }
    case SupportedLanguages.IT: {
      return intl.formatMessage({ id: 'general.label.italian' })
    }
    case SupportedLanguages.NL: {
      return intl.formatMessage({ id: 'general.label.dutch' })
    }
    default: {
      return language
    }
  }
}

export const ProductReviewsLanguageSwitcher: FC<{
  currentReviewsLanguage: string
  selectableLanguages: {
    reviewsCount: number
    text: string
    value: string
  }[]
  setLanguage: (value: string) => void
}> = ({ currentReviewsLanguage, selectableLanguages, setLanguage }) => {
  const intl = useIntl()

  const reviewCountForCurrentLanguage = selectableLanguages.find((it) => {
    return it.value === currentReviewsLanguage
  })?.reviewsCount

  return (
    <div className="mt-3.5 flex flex-col items-start tablet:flex-row tablet:items-center">
      <h2 className="mb-2 mr-2 whitespace-nowrap text-xl tablet:mb-0">
        <FormattedMessage id="general.label.productReviews.selectLanguage" />
      </h2>
      <Select
        className="w-full rounded-md bg-light-primary-medium aria-[expanded=true]:rounded-md"
        onChange={(event, value): void => {
          if (!value) {
            return
          }
          setLanguage(value)
        }}
        renderValue={(): ReactNode => {
          return (
            <div
              data-qa-id={`rating-count-${getLabelText(
                intl,
                currentReviewsLanguage,
              )}-(${reviewCountForCurrentLanguage})`}
            >
              {`${getLabelText(
                intl,
                currentReviewsLanguage,
              )} (${reviewCountForCurrentLanguage})`}
            </div>
          )
        }}
        slotProps={{
          popup: {
            className:
              'data-[popup-placement=bottom-start]:rounded-b-md data-[popup-placement=top-start]:rounded-t-md',
          },
          root: {
            'aria-label': intl.formatMessage({
              id: 'general.label.productReviews.selectLanguage',
            }),
          },
        }}
        value={currentReviewsLanguage}
      >
        {selectableLanguages.map((it) => {
          return (
            <Option key={it.value} value={it.value}>
              <span className="flex w-full justify-start text-dark-brand">
                {`${getLabelText(intl, it.value)} (${it.reviewsCount})`}
              </span>
            </Option>
          )
        })}
      </Select>
    </div>
  )
}
